import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UserPlus, Check, X, Mail, Phone, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function SignupRequestsPage() {
  const [reqs, setReqs] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    const r = await api.get(`/signup-requests${filter ? `?status=${filter}` : ""}`);
    setReqs(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (r, assigned_role) => {
    await api.patch(`/signup-requests/${r.id}`, { status: "approved", assigned_role });
    toast.success(`Approved ${r.name} as ${assigned_role}`);
    load();
  };
  const reject = async (r) => {
    const reason = window.prompt("Reason for rejection (optional):", "");
    if (reason === null) return;
    await api.patch(`/signup-requests/${r.id}`, { status: "rejected", rejected_reason: reason });
    toast.success("Request rejected");
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="signups-page">
      <div className="mb-8">
        <div className="label-eyebrow">Access Requests</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2 flex items-center gap-3">
          <UserPlus className="w-8 h-8 text-[#D4AF37]" /> Signup Queue
        </h1>
        <p className="text-zinc-500 text-sm mt-2">People who applied to join HNC via the public sign-up page.</p>
      </div>

      <div className="flex gap-2 mb-6">
        {["pending", "approved", "rejected", ""].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`filter-${s || "all"}`}
            className={`px-3 py-2 text-xs rounded-sm capitalize ${filter === s ? "bg-[#D4AF37] text-black" : "border border-[#2a2a2a] text-zinc-400 hover:text-white"}`}>
            {s || "All"}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {reqs.length === 0 && (
          <div className="card-lux p-10 text-center">
            <AlertCircle className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            <h3 className="font-display text-xl">No {filter || ""} requests</h3>
            <p className="text-zinc-500 text-sm mt-1">All caught up — check back when someone applies.</p>
          </div>
        )}
        {reqs.map((r) => (
          <div key={r.id} className="card-lux p-5" data-testid={`req-${r.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl">{r.name}</div>
                <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {r.email}</span>
                  {r.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                  <span>· {fmtDate(r.created_at)}</span>
                </div>
                {r.reason && (
                  <div className="mt-3 p-3 bg-[#0a0a0a] border border-[#1a1a1a] rounded-sm text-sm text-zinc-300 italic">
                    "{r.reason}"
                  </div>
                )}
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="text-zinc-500">Requested role:</span>
                  <span className="stage-chip capitalize text-[10px]" style={{ color: "#D4AF37", borderColor: "#D4AF3755" }}>{r.requested_role}</span>
                </div>
              </div>
              {r.status === "pending" ? (
                <div className="flex flex-col gap-2 min-w-[200px]">
                  <ApproveDropdown defaultRole={r.requested_role} onApprove={(role) => approve(r, role)} />
                  <Button onClick={() => reject(r)} data-testid={`reject-${r.id}`} variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-sm">
                    <X className="w-3.5 h-3.5 mr-1.5" /> Reject
                  </Button>
                </div>
              ) : (
                <div className="text-right">
                  <span className={`stage-chip capitalize ${r.status === "approved" ? "text-emerald-400 border-emerald-500/30" : "text-red-400 border-red-500/30"}`}>{r.status}</span>
                  {r.decided_at && <div className="text-[10px] text-zinc-600 mt-2">{fmtDate(r.decided_at)}</div>}
                  {r.rejected_reason && <div className="text-xs text-zinc-500 mt-2 italic max-w-[260px]">"{r.rejected_reason}"</div>}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApproveDropdown({ defaultRole, onApprove }) {
  const [role, setRole] = useState(defaultRole);
  return (
    <div className="flex gap-2">
      <select value={role} onChange={(e) => setRole(e.target.value)} className="flex-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-2 h-9 text-xs capitalize">
        {["admin", "operations", "kitchen", "driver", "setup", "customer"].map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <Button onClick={() => onApprove(role)} data-testid="approve-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white h-9">
        <Check className="w-3.5 h-3.5 mr-1" /> Approve
      </Button>
    </div>
  );
}
